import { SectionCard } from '@/components/section-card';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { NumberInput } from '@/components/ui/number-input';
import {
  variantUtils,
  type VariantConfig,
  type SplitType,
  VARIANT_COUNTS
} from './split-configuration.utils';

type SplitConfigurationProps = {
  value: VariantConfig;
  onChange: (config: VariantConfig) => void;
  disabled?: boolean;
};

export function SplitConfiguration({ value, onChange, disabled }: SplitConfigurationProps) {
  const handleVariantCount = (count: number) => {
    onChange(variantUtils.adjustVariantCount(value, count));
  };

  const handleVariantWeight = (variantId: string, newWeight: number | null) => {
    onChange(variantUtils.updateVariantWeight(value, variantId, newWeight));
  };

  const handleSplitType = (newType: SplitType) => {
    onChange(variantUtils.changeSplitType(value, newType));
  };

  return (
    <SectionCard title="Split Send">
      <div className="flex items-center justify-between space-x-4">
        <div className="space-y-0.5">
          <Label>Send multiple variants of your campaign</Label>
        </div>
        <Switch
          checked={value.isEnabled}
          onCheckedChange={(checked) => {
            onChange({ ...value, isEnabled: checked });
          }}
          disabled={disabled}
        />
      </div>

      {value.isEnabled && (
        <div className="space-y-6">
          <VariantCountSelector
            count={value.variants.length}
            onChange={handleVariantCount}
            disabled={disabled}
          />

          <DistributionTypeSelector
            value={value.splitType}
            onChange={handleSplitType}
            disabled={disabled}
          />

          <VariantWeights
            variants={value.variants}
            splitType={value.splitType}
            onWeightChange={handleVariantWeight}
            disabled={disabled}
          />
        </div>
      )}
    </SectionCard>
  );
}

interface VariantCountSelectorProps {
  count: number;
  onChange: (count: number) => void;
  disabled?: boolean;
}

function VariantCountSelector({ count, onChange, disabled }: VariantCountSelectorProps) {
  return (
    <div className="space-y-2">
      <Label>Number of Message Variations</Label>
      <Select
        value={count.toString()}
        onValueChange={(value) => {
          onChange(parseInt(value));
        }}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {VARIANT_COUNTS.map((count) => (
            <SelectItem key={count} value={count.toString()}>
              {count}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface DistributionTypeSelectorProps {
  value: SplitType;
  onChange: (type: SplitType) => void;
  disabled?: boolean;
}

function DistributionTypeSelector({ value, onChange, disabled }: DistributionTypeSelectorProps) {
  return (
    <div className="space-y-2">
      <RadioGroup value={value} onValueChange={onChange} className="flex gap-4" disabled={disabled}>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="even" id="even" />
          <Label htmlFor="even">Even Split</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="custom" id="custom" />
          <Label htmlFor="custom">Custom Split</Label>
        </div>
      </RadioGroup>
    </div>
  );
}

interface VariantWeightsProps {
  variants: VariantConfig['variants'];
  splitType: SplitType;
  onWeightChange: (id: string, weight: number | null) => void;
  disabled?: boolean;
}

function VariantWeights({ variants, splitType, onWeightChange, disabled }: VariantWeightsProps) {
  const handleWeightChange = (variantId: string, newWeight: number | null) => {
    // For non-null weights, ensure they're between 0 and 100
    const validWeight = newWeight === null ? null : Math.max(0, Math.min(100, newWeight));
    onWeightChange(variantId, validWeight);
  };

  const totalWeight = variants.reduce((sum, variant) => sum + (variant.weight ?? 0), 0);
  const hasEmptyWeights = variants.some((v) => v.weight === null);
  const isValid = !hasEmptyWeights && totalWeight === 100;

  return (
    <div className="space-y-4">
      {variants.map((variant, index) => (
        <div key={variant.id} className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Variant {index + 1}</Label>
            <span className={cn('text-sm', !isValid && 'text-destructive-foreground')}>
              {variant.weight === null ? '-' : `${variant.weight}%`}
            </span>
          </div>
          <NumberInput
            value={variant.weight}
            onChange={(value) => {
              handleWeightChange(variant.id, value);
            }}
            disabled={splitType !== 'custom' || disabled}
            min={0}
            max={100}
            className={cn(!isValid && 'border-destructive-foreground')}
          />
        </div>
      ))}

      {!isValid && (
        <div className="text-sm text-destructive-foreground">
          {hasEmptyWeights
            ? 'Please fill in all weights'
            : `Total weight must equal 100% (currently ${totalWeight}%)`}
        </div>
      )}
    </div>
  );
}
